import React, { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Navbar from "@/components/Navbar";
import BookingDialog from "@/components/BookingDialog";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Testimonials from "@/components/Testimonials";
import Practitioner from "@/components/Practitioner";
import Manifesto from "@/components/Manifesto";
import Marquee from "@/components/Marquee";
import PaymentSection from "@/components/PaymentSection";
import Products from "@/components/Products";
import AboutBachFlower from "@/components/AboutBachFlower";
import HeroPhilosophyTeaser from "@/components/HeroPhilosophyTeaser";
import RemedyQuiz from "@/components/RemedyQuiz";
import RemediesGallery from "@/components/RemediesGallery";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ExternalLink,
  ArrowRight,
  Sparkles,
  Leaf,
  Heart,
  Sprout,
  Users,
  Briefcase,
  Compass,
  Baby,
  Check,
  Phone,
  Mail,
  Quote,
  ArrowDown,
  MapPin,
  Instagram,
  Star,
} from "lucide-react";
import { ASSETS, PHONE, WHATSAPP_LINK, GOOGLE_BUSINESS_URL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/site";
import { ACCORDIONS, SEGMENTS, SESSIONS, MARQUEE } from "@/lib/content";
import { Reveal, MaskLines, EASE } from "@/lib/motion";

const segmentIcons = {
  children: Baby,
  relationships: Heart,
  work: Briefcase,
  transitions: Compass,
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE, delay: 0.5 + i * 0.12 },
  }),
};

export default function Landing() {
  const [bookOpen, setBookOpen] = useState(false);
  const openBooking = () => setBookOpen(true);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textureY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div id="top" className="min-h-screen bg-[#FDFBF7] text-[#0B302F]">
      <Navbar onBookNow={openBooking} />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            y: textureY,
            backgroundImage: `url(${ASSETS.heroTexture})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.6 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute -top-24 -left-20 w-80 h-80 rounded-full bg-[#F9EAE8] blur-3xl pointer-events-none"
        />
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
          className="absolute -bottom-24 -right-20 w-96 h-96 rounded-full bg-[#E7D5A8] blur-3xl pointer-events-none"
        />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-8 sm:pt-14 pb-10 sm:pb-16 grid lg:grid-cols-12 gap-8 items-center"
        >
          <div className="lg:col-span-7">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-[#EAE4DA] mb-5"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#D5B876]" strokeWidth={1.5} />
              <span className="eyebrow">A Journey to Natural Wellness</span>
            </motion.div>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B302F] tracking-tight">
              <MaskLines
                lines={["Gentle paths to bloom", "through stress and", "emotional challenges"]}
                lineClassName=""
              />
            </h1>

            <motion.p
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-5 text-base sm:text-lg text-[#0B302F]/75 font-sans max-w-xl leading-relaxed"
            >
              <i>Bloom &amp; Heal</i> offers a compassionate, holistic space for emotional balance through
              Bach Flower Remedies — a natural, gentle, and non-invasive system of 38 essences.
            </motion.p>

            <motion.div
              custom={3}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-6 flex flex-wrap items-center gap-3"
            >
              <Button
                onClick={openBooking}
                data-testid="hero-book-now"
                size="lg"
                className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] px-7 h-11 text-xs tracking-[0.22em] uppercase group"
              >
                Book a Session
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Button>
              <a
                href={ASSETS.brochureView}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-brochure-view"
                className="inline-flex items-center justify-center rounded-full border border-[#0B302F] text-[#0B302F] hover:bg-[#F9EAE8] px-7 h-11 text-xs tracking-[0.22em] uppercase font-medium transition-colors"
              >
                View Brochure <ExternalLink className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </a>
            </motion.div>

            <motion.div
              custom={4}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-6 flex items-center gap-6 sm:gap-8 text-[#0B302F]/70 text-sm"
            >
              <div className="flex items-center gap-2">
                <Leaf className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                <span>100% Natural</span>
              </div>
              <div className="h-3 w-px bg-[#EAE4DA]" />
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                <span>Non-invasive</span>
              </div>
              <div className="h-3 w-px bg-[#EAE4DA]" />
              <div className="flex items-center gap-2">
                <Sprout className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                <span>For all ages</span>
              </div>
            </motion.div>

            <HeroPhilosophyTeaser />
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: logoY, scale: logoScale }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, ease: EASE, delay: 0.3 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-[#F9EAE8]/60 to-[#E7D5A8]/40 rounded-[2.5rem] blur-2xl" />
              <img
                src={ASSETS.logo}
                alt="Bloom & Heal — Bach Flower Remedies"
                className="relative w-full max-w-md mx-auto rounded-[2rem] ring-1 ring-[#D5B876]/40 shadow-xl shadow-[#0B302F]/10"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
              className="hidden lg:block absolute -bottom-6 -left-6 max-w-[220px] bg-white/90 backdrop-blur p-4 rounded-2xl border border-[#EAE4DA] shadow-lg"
            >
              <Quote className="h-4 w-4 text-[#D5B876] mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#0B302F] font-serif italic leading-snug font-medium">
                “Heal thyself by recognising the harmony of body and soul.”
              </p>
              <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#2A5C5A]">— Dr. Edward Bach</div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-[#2A5C5A]"
        >
          <span className="text-[10px] tracking-[0.28em] uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="py-8 sm:py-10 border-y border-[#EAE4DA] bg-[#F9EAE8]/30">
        <Marquee items={MARQUEE} />
      </div>

      {/* ABOUT BACH FLOWER (editorial) */}
      <AboutBachFlower />

      {/* THE 38 REMEDIES GALLERY */}
      <RemediesGallery />

      {/* REMEDY FINDER QUIZ */}
      <RemedyQuiz onBookNow={openBooking} />

      {/* ACCORDIONS — deeper dive */}
      <section id="explore" className="py-20 sm:py-28 bg-[#F6F2EB]/50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <div className="eyebrow mb-3">A deeper look</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight">
              Explore the emotional landscape.
            </h2>
            <p className="mt-4 text-[#0B302F]/70 max-w-2xl mx-auto">
              Tap each section to see the feelings, patterns and life themes Bach Flower Remedies gently support.
            </p>
          </Reveal>

          <Reveal y={36}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="about-accordion">
              {ACCORDIONS.map((a) => (
                <AccordionItem
                  key={a.id}
                  value={a.id}
                  data-testid={`accordion-item-${a.id}`}
                  className="border border-[#EAE4DA] bg-white/60 rounded-2xl px-5 sm:px-7 data-[state=open]:bg-white data-[state=open]:shadow-sm transition-colors"
                >
                  <AccordionTrigger
                    data-testid={`accordion-trigger-${a.id}`}
                    className="font-serif text-xl sm:text-2xl text-[#0B302F] hover:no-underline py-5"
                  >
                    {a.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#0B302F]/80 pb-6 font-sans leading-relaxed">
                    {a.body && (
                      <div className="space-y-3 max-w-2xl">
                        {a.body.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </div>
                    )}
                    {a.list && (
                      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-1">
                        {a.list.map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D5B876] shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <Manifesto />

      {/* WHO IS THIS FOR */}
      <section id="audience" className="py-20 sm:py-28 bg-[#F6F2EB]/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-12">
            <div className="eyebrow mb-3">Who this support is for</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight max-w-3xl">
              Gentle care across every season of life.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SEGMENTS.map((s, idx) => {
              const Icon = segmentIcons[s.id] || Users;
              const tints = ["bg-[#F9EAE8]/60", "bg-white", "bg-[#F9EAE8]/40", "bg-white"];
              return (
                <motion.div
                  key={s.id}
                  data-testid={`segment-card-${s.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: (idx % 2) * 0.1 }}
                  className={`group p-7 sm:p-9 rounded-3xl border border-[#EAE4DA] ${tints[idx % 4]} hover:border-[#D5B876] transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-[#0B302F]/5 flex items-center justify-center ring-1 ring-[#0B302F]/10 transition-transform duration-300 group-hover:scale-110">
                      <Icon className="h-5 w-5 text-[#2A5C5A]" strokeWidth={1.4} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl sm:text-3xl text-[#0B302F] leading-snug">{s.title}</h3>
                      <p className="mt-3 text-[#0B302F]/75 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRACTITIONER */}
      <Practitioner onBookNow={openBooking} />

      {/* SESSIONS */}
      <section id="sessions" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6" y={40}>
            <div className="relative group">
              <div className="absolute -inset-3 bg-[#F9EAE8]/60 rounded-[2rem] blur-xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] ring-1 ring-[#EAE4DA]">
                <img
                  src={ASSETS.calmWoman}
                  alt="A calm therapy session"
                  className="object-cover w-full h-[460px] transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B302F]/20 to-transparent" />
              </div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" y={40} delay={0.1}>
            <div className="eyebrow mb-3">Sessions available</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight">
              A practice tailored to <em className="text-[#D5B876] not-italic">you</em>.
            </h2>
            <p className="mt-5 text-[#0B302F]/75 max-w-xl">
              Each consultation results in a personalised remedy bottle of up to seven essences,
              chosen specifically for your emotional landscape.
            </p>
            <ul className="mt-8 space-y-3">
              {SESSIONS.map((s, i) => (
                <li
                  key={i}
                  data-testid={`session-item-${i}`}
                  className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-[#EAE4DA]"
                >
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#0B302F] text-[#FDFBF7] flex items-center justify-center">
                    <Check className="h-3.5 w-3.5" strokeWidth={2} />
                  </span>
                  <span className="text-[#0B302F]/85 font-sans">{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                onClick={openBooking}
                data-testid="sessions-book-now"
                size="lg"
                className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] px-7 h-12 text-xs tracking-[0.22em] uppercase"
              >
                Book a Session
              </Button>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sessions-whatsapp"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full border-[#0B302F] text-[#0B302F] hover:bg-[#F9EAE8] bg-transparent px-7 h-12 text-xs tracking-[0.22em] uppercase"
                >
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REMEDY / PAYMENT */}
      <PaymentSection onBookNow={openBooking} />

      {/* READY MIXES / SHOP */}
      <Products />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* BROCHURE */}
      <section id="brochure" className="py-20 sm:py-28 bg-[#0B302F] text-[#FDFBF7] relative overflow-hidden grain">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D5B876]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#2A5C5A]/40 blur-3xl pointer-events-none" />
        <Reveal className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="eyebrow text-[#D5B876] mb-3">The Brochure</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
            Take the essence of Bach Flower Remedies with you.
          </h2>
          <p className="mt-5 text-[#FDFBF7]/80 max-w-2xl mx-auto">
            A beautifully prepared flyer covering the 38 remedies, their emotional themes,
            and how a personalised session works.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <a
              href={ASSETS.brochureView}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brochure-view"
              className="inline-flex items-center justify-center rounded-full bg-[#D5B876] text-[#0B302F] hover:bg-[#E7D5A8] px-7 h-12 text-xs tracking-[0.22em] uppercase font-medium transition-colors"
            >
              <ExternalLink className="mr-2 h-4 w-4" strokeWidth={1.5} /> View Brochure
            </a>
          </div>
        </Reveal>
      </section>

      {/* CONTACT / FOOTER */}
      <footer className="py-16 bg-[#FDFBF7] border-t border-[#EAE4DA]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-10 items-start">
          <div>
            <div className="flex items-center gap-3">
              <img
                src={ASSETS.logo}
                alt="Bloom & Heal"
                className="h-12 w-12 rounded-full object-cover ring-1 ring-[#D5B876]/40"
              />
              <div>
                <div className="font-serif text-2xl text-[#0B302F]">Bloom &amp; Heal</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#2A5C5A] mt-0.5">
                  Bach Flower Remedies
                </div>
              </div>
            </div>
            <p className="mt-4 text-[#0B302F]/70 text-sm max-w-xs leading-relaxed">
              A nurturing space for emotional balance, healing and inner growth.
            </p>
          </div>

          <div>
            <div className="eyebrow mb-3">Reach out</div>
            <a
              href={`tel:${PHONE}`}
              data-testid="footer-phone"
              className="flex items-center gap-2 text-[#0B302F] hover:text-[#2A5C5A]"
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} /> {PHONE}
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="flex items-center gap-2 text-[#0B302F] hover:text-[#2A5C5A] mt-2"
            >
              <Mail className="h-4 w-4" strokeWidth={1.5} /> Chat on WhatsApp
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="flex items-center gap-2 text-[#0B302F] hover:text-[#2A5C5A] mt-2"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} /> {INSTAGRAM_HANDLE}
            </a>
            <a
              href={GOOGLE_BUSINESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google"
              className="flex items-center gap-2 text-[#0B302F] hover:text-[#2A5C5A] mt-2"
            >
              <Star className="h-4 w-4 text-[#D5B876] fill-[#D5B876]" strokeWidth={1.4} /> Review us on Google
            </a>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Elicure+Healthcare+Coral+Classic+Commercial+Complex+Chembur+Mumbai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-address"
              className="flex items-start gap-2 text-[#0B302F] hover:text-[#2A5C5A] mt-3 text-sm leading-relaxed"
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>
                <span className="font-medium">Elicure Healthcare</span>
                <br />303, 304 Coral Classic Commercial Complex,
                <br />Chembur, Mumbai 400071
              </span>
            </a>
          </div>

          <div>
            <div className="eyebrow mb-3">Begin your journey</div>
            <Button
              onClick={openBooking}
              data-testid="footer-book-now"
              className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] text-xs tracking-[0.22em] uppercase px-6"
            >
              Book a Session
            </Button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-12 pt-6 border-t border-[#EAE4DA] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#0B302F]/60">
          <div>© {new Date().getFullYear()} <i>Bloom &amp; Heal</i>. All rights reserved.</div>
          <div className="font-serif italic text-[#2A5C5A]">Reconnecting to your innate ability to heal.</div>
        </div>
      </footer>

      <WhatsAppFloat />
      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
